/**
 * DropReveal — a "fly to slot" landing for a freshly DROPPED widget/section.
 *
 * Instead of appearing in place, the new item travels from the drop point (the
 * cursor) to its final grid slot and settles — FLIP (First-Last-Invert-Play):
 * measure the final rect, invert to the drop origin, then play back to rest.
 *
 * Only genuine user drops animate: the drop handler calls `markDropped(id,
 * origin)` with the client-space drop point, and the item's next mount consumes
 * it exactly once (so load / scroll / zoom-LOD remounts never replay it); the
 * flag also auto-expires. The animation is on the item's INNER content — the
 * grid engine independently animates the surrounding siblings' reflow, so the
 * two layers compose without fighting.
 *
 * Web Animations API (not CSS): `fill:none` reverts cleanly to no inline
 * transform, and there's nothing to time-manage on cleanup.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { css, html } from "react-strict-dom";

interface DropOrigin {
  /** Client-space point the item should appear to fly from (the drop pointer). */
  x: number;
  y: number;
}

const dropOrigins = new Map<string, DropOrigin>();

/** Mark an item id as just-dropped from `origin` → its next mount flies in. */
export function markDropped(id: string, origin: DropOrigin): void {
  dropOrigins.set(id, origin);
  setTimeout(() => dropOrigins.delete(id), 1200);
}

const FLY_MS = 360;
// The grid engine's own drop-settle curve (back-out ~4% overshoot) — so a new
// item lands with the same feel as the siblings reflowing around it.
const FLY_EASING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

/** The canvas zoom, read from the xyflow viewport's transform matrix. The item
 *  lives inside that scaled layer, so a client-space delta must be divided by
 *  the zoom to get the LOCAL translate that produces it. Imperative (no hook) so
 *  it doesn't subscribe every widget to zoom changes. */
function readCanvasZoom(el: Element): number {
  const vp = el.closest(".react-flow__viewport");
  if (!vp) return 1;
  const t = getComputedStyle(vp).transform;
  const m = t?.match(/matrix\(([^)]+)\)/);
  if (m) {
    const a = parseFloat(m[1]!.split(",")[0]!); // matrix(a,…) → scaleX
    if (a > 0) return a;
  }
  return 1;
}

export function DropReveal({
  itemId,
  children,
}: {
  itemId: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Consume the origin once, at mount, so `origin` is stable for this
  // component's life (the wrapper never toggles → the widget never remounts).
  const [origin] = useState<DropOrigin | null>(() => {
    const o = dropOrigins.get(itemId);
    if (o) {
      dropOrigins.delete(itemId);
      return o;
    }
    return null;
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !origin) return;
    // Cancel any animation already on THIS element before measuring — under
    // StrictMode the effect runs twice, and a running FLIP transform would make
    // getBoundingClientRect report the element AT the drop point, yielding a
    // bogus ~0 delta on the second pass that clobbers the real one. Cleanup
    // cancels between passes so the re-measure is always against the resting box.
    el.getAnimations().forEach((a) => a.cancel());
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const rect = el.getBoundingClientRect();
    let anim: Animation;
    if (reduce || rect.width === 0 || rect.height === 0) {
      anim = el.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 160,
        easing: "ease-out",
      });
    } else {
      const zoom = readCanvasZoom(el);
      const dx = (origin.x - (rect.left + rect.width / 2)) / zoom;
      const dy = (origin.y - (rect.top + rect.height / 2)) / zoom;
      anim = el.animate(
        [
          {
            transform: `translate(${dx}px, ${dy}px) scale(0.9)`,
            opacity: 0.25,
          },
          { transform: "translate(0px, 0px) scale(1)", opacity: 1 },
        ],
        { duration: FLY_MS, easing: FLY_EASING, fill: "none" },
      );
    }
    return () => anim.cancel();
  }, [origin]);

  if (!origin) return <>{children}</>;
  return (
    <html.div ref={ref} style={styles.wrap}>
      {children}
    </html.div>
  );
}

const styles = css.create({
  wrap: {
    height: "100%",
    width: "100%",
    transformOrigin: "center center",
    // Keep the cell's vertical placement (a button's `valign`) for what it
    // wraps: this box fills the cell, so the placement must pass on.
    alignContent: "inherit",
  },
});
